import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, sortByName} from "./utils";

const initialListState = {
    restaurant: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    branch: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    category: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    tag: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    product: {
        items: {},
        itemsWithTags: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    user: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    userRole: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    subscription: {
        items: {},
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    customer: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    vendor: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    internalBranch: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    internalEmployee: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    notApplicable: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    income: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    expense: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    gst: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    bankList: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
    vendorListDoc: {
        items: [],
        status: "idle", // 'idle' | 'success' | 'failed'
        error: null,
    },
};

// Fetch Restaurant List
export const fetchRestaurantList = createAsyncThunk("list/fetchRestaurantList", async () => {
        try {
            const response = await api.get("/list/restaurant");
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Branch List
export const fetchBranchList = createAsyncThunk("list/fetchBranchList", async (restaurantId) => {
        try {
            const response = await api.get(`/list/branch/${restaurantId}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Category List
export const fetchCategoryList = createAsyncThunk("list/fetchCategoryList", async (id) => {
        try {
            const response = await api.get(`/list/categories/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch tag list
export const fetchTagList = createAsyncThunk("list/fetchTagList", async ({scope, roleSign, scopeId} = {}) => {
    try {
        let response;

        if (scopeId) {
            if (scope?.toLowerCase() === "restaurant") {
                response = await api.get(`${roleSign}/list/restauranttag?id=${scopeId}`);
            } else {
                response = await api.get(`${roleSign}/list/tags?id=${scopeId}`);
            }
        } else {
            response = await api.get(`/list/tag`);
        }
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

// Fetch Product List
export const fetchProductList = createAsyncThunk("list/fetchProductList", async ({
                                                                                     scope,
                                                                                     roleSign,
                                                                                     scopeId,
                                                                                     tagId = undefined,
                                                                                     allProducts = undefined,
                                                                                     internalProducts = undefined,
                                                                                     vendor = undefined
                                                                                 }) => {

        try {
            let response;
            if (scope.toLowerCase() === "restaurant") {
                response = await api.get(`/${roleSign}/list/rest/products/${tagId}?id=${scopeId}`);
            } else if (allProducts) {
                response = await api.get(`/${roleSign}/list/allproducts?id=${scopeId}`);
            } else if (internalProducts) {
                response = await api.get(`/${roleSign}/list/internalproducts?id=${scopeId}`);
            } else if (vendor) {
                response = await api.get(`/${roleSign}/vendors/productList?id=${scopeId}&vendor=${vendor}`);
            } else {
                response = await api.get(`/${roleSign}/list/products?id=${scopeId}`);
            }
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Product List
export const fetchUserList = createAsyncThunk("list/fetchUserList", async () => {
        try {
            const response = await api.get("/list/user");
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch user Role List
export const fetchUserRoleList = createAsyncThunk("list/fetchUserRoleList", async () => {
        try {
            const response = await api.get("/list/role");
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch user Subscription List
export const fetchSubscriptionList = createAsyncThunk("list/fetchSubscriptionList", async () => {
        try {
            const response = await api.get("list/plans");
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);


// Fetch Customer List
export const fetchCustomerList = createAsyncThunk("list/fetchCustomerList", async ({scopeId, roleSign}) => {
        try {
            const response = await api.get(`${roleSign}/list/customer?id=${scopeId}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Vendor List
export const fetchVendorList = createAsyncThunk("list/fetchVendorList", async ({scopeId, roleSign}) => {
        try {
            const response = await api.get(`/list/vendor?id=${scopeId}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Internal Branch List
export const fetchInternalBranchList = createAsyncThunk("list/fetchInternalBranchList", async ({scopeId, roleSign}) => {
        try {
            const response = await api.get(`${roleSign}/list/InternalBranch?id=${scopeId}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);
// Fetch Employee List
export const fetchInternalEmployeeList = createAsyncThunk("list/fetchInternalEmployeeList", async ({
                                                                                                       branchId,
                                                                                                       roleSign
                                                                                                   }) => {
        try {
            const response = await api.get(`${roleSign}/list/employeeList?_id=${branchId}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Not Applicable List
export const fetchNotApplicableList = createAsyncThunk("transactionType/fetchNotApplicableList", async () => {
        try {
            const response = await api.get(`list/transactionType/notApplicable`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch Expense List
export const fetchExpenseList = createAsyncThunk("transactionType/fetchExpenseList", async () => {
        try {
            const response = await api.get(`list/transactionType/expense`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch gst List
export const fetchGstList = createAsyncThunk("list/fetchGstList", async () => {
        try {
            const response = await api.get(`/list/gst`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);


// Fetch bank List
export const fetchBankList = createAsyncThunk("list/fetchBankList", async ({roleSign, scopeId}) => {
        try {
            const response = await api.get(`${roleSign}/list/banklist?id=${scopeId}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch vendor List for vendor upload
export const fetchVendorListForVendorDoc = createAsyncThunk("list/fetchVendorListForVendorDoc", async ({scopeId}) => {
        try {
            const response = await api.get(`/vendor/list?id=${scopeId}`, {
                vendorToken: 'vendorAuthToken',// overrides default token
            });
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);


const listSlice = createSlice({
    name: "list",
    initialState: initialListState,
    reducers: {},
    extraReducers(builder) {
        builder
            // Handle Restaurant List
            .addCase(fetchRestaurantList.fulfilled, (state, action) => {
                state.restaurant = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.restaurant.items = fetchStatus === "success" ? action.payload.data.data : state.restaurant.items;
                state.restaurant.status = fetchStatus;
                state.restaurant.error = error;
            })
            // Handle Branch List
            .addCase(fetchBranchList.fulfilled, (state, action) => {
                state.branch = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.branch.items = fetchStatus === "success" ? action.payload.data.data : state.branch.items;
                state.branch.status = fetchStatus;
                state.branch.error = error;
            })
            // Handle Category List
            .addCase(fetchCategoryList.fulfilled, (state, action) => {
                state.category = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.category.items = fetchStatus === "success" ? action.payload.data.data : state.category.items;
                state.category.status = fetchStatus;
                state.category.error = error;
            })
            // Handle tag List
            .addCase(fetchTagList.fulfilled, (state, action) => {
                state.tag = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.tag.items = fetchStatus === "success" ? sortByName(action.payload.data.data) : state.tag.items;
                state.tag.status = fetchStatus;
                state.tag.error = error;
            })
            // Handle Product List
            .addCase(fetchProductList.fulfilled, (state, action) => {
                state.product.items = [];
                state.product.selected = [];
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                const list = action.payload?.data?.data ?? [];
                const sortList = [...list].sort((a, b) => a.name.localeCompare(b.name));
                state.product.itemsWithTags = fetchStatus === "success" ? sortList : state.product.itemsWithTags;
                state.product.items = fetchStatus === "success" ? sortList.reduce((acc, item) => {
                    acc[item._id] = item.name;
                    return acc;
                }, {}) : state.product.items;
                state.product.status = fetchStatus;
                state.product.error = error;
            })
            // Handle Customer List
            .addCase(fetchUserList.fulfilled, (state, action) => {
                state.customer = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.user.items = fetchStatus === "success" ? action.payload.data.data : state.user.items;
                state.user.status = fetchStatus;
                state.user.error = error;
            })
            // Handle User role List
            .addCase(fetchUserRoleList.fulfilled, (state, action) => {
                state.userRole = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.userRole.items = fetchStatus === "success" ? action.payload.data.data : state.userRole.items;
                state.userRole.status = fetchStatus;
                state.userRole.error = error;
            })
            // Handle Subscription List
            .addCase(fetchSubscriptionList.fulfilled, (state, action) => {
                state.subscription = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.subscription.items = fetchStatus === "success" ? action.payload.data.data : state.subscription.items;
                state.subscription.status = fetchStatus;
                state.subscription.error = error;
            })
            // Handle Customer List
            .addCase(fetchCustomerList.fulfilled, (state, action) => {
                state.customer = {};
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.customer.items = fetchStatus === "success" ? action.payload.data.data : state.customer.items;
                state.customer.status = fetchStatus;
                state.customer.error = error;
            })
            // Handle Vendor List
            .addCase(fetchVendorList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.vendor.items = fetchStatus === "success" ? action.payload.data.data : state.vendor.items;
                state.vendor.status = fetchStatus;
                state.vendor.error = error;
            })

            // Handle Internal Branch List
            .addCase(fetchInternalBranchList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.internalBranch.items = fetchStatus === "success" ? action.payload.data.data : state.internalBranch.items;
                state.internalBranch.status = fetchStatus;
                state.internalBranch.error = error;
            })

            //Handle notApplicable list
            .addCase(fetchNotApplicableList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.notApplicable.items = fetchStatus === "success" ? action.payload.data.data : state.notApplicable.items;
                state.notApplicable.status = fetchStatus;
                state.notApplicable.error = error;
            })

            //Handle expense list
            .addCase(fetchExpenseList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.expense.items = fetchStatus === "success" ? action.payload.data.data : state.expense.items;
                state.expense.status = fetchStatus;
                state.expense.error = error;
            })

            // Handle Employee List
            .addCase(fetchInternalEmployeeList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.internalEmployee.items = fetchStatus === "success" ? action.payload.data.data : state.internalEmployee.items;
                state.internalEmployee.status = fetchStatus;
                state.internalEmployee.error = error;
            })

            // Handle Gst List
            .addCase(fetchGstList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.gst.items = fetchStatus === "success" ? action.payload.data.data : state.gst.items;
                state.gst.status = fetchStatus;
                state.gst.error = error;
            })

            // Handle vendor List for vendor Doc
            .addCase(fetchVendorListForVendorDoc.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.vendorListDoc.items = fetchStatus === "success" ? action.payload.data.data : state.vendorListDoc.items;
                state.vendorListDoc.status = fetchStatus;
                state.vendorListDoc.error = error;
            })

            // Handle Gst List
            .addCase(fetchBankList.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, "fetchStatus");
                state.bankList.items = fetchStatus === "success" ? action.payload.data.data : state.bankList.items;
                state.bankList.status = fetchStatus;
                state.bankList.error = error;
            });
    },
});

export default listSlice.reducer;
